import { Pipe, PipeTransform } from '@angular/core';

/**
 * Shortens a counter so it fits a fixed-width card footer.
 *
 * The V3 candidate/job cards sit in a 316px rail ($jc-v3-left-w). Their
 * counter row holds up to five tiles (Views / Likes / Invites / Applied /
 * Chat), which leaves roughly 272px of usable width. A raw eight-digit count
 * needs about 62px per tile, so five of them overflow and the row either
 * wraps -- changing the card's height mid-list -- or has to scroll.
 *
 * Counts below 1000 are left exactly as they are, so the common case is
 * untouched. Above that they collapse to K / M / B with at most one decimal,
 * and the trailing ".0" is dropped so "5000" reads "5K" rather than "5.0K".
 * Callers keep the exact figure in the tile's `title`, so the precise number
 * is always one hover away.
 */
@Pipe({
  name: 'compactCount',
  standalone: true
})
export class CompactCountPipe implements PipeTransform {
  private static readonly UNITS = [
    { limit: 1e9, suffix: 'B' },
    { limit: 1e6, suffix: 'M' },
    { limit: 1e3, suffix: 'K' }
  ];

  transform(value: number | string | null | undefined): string {
    const n = Number(value);

    // Null, undefined, '' and any non-numeric input all render as '0' —
    // the templates already fall back to `|| 0`, this keeps parity for
    // anything that slips through as a string.
    if (value === null || value === undefined || value === '' || isNaN(n)) {
      return '0';
    }

    const sign = n < 0 ? '-' : '';
    const abs = Math.abs(n);

    if (abs < 1e3) {
      return sign + String(abs);
    }

    for (const { limit, suffix } of CompactCountPipe.UNITS) {
      if (abs >= limit) {
        // Truncate rather than round, so a tile never claims a milestone the
        // underlying number has not reached (999_999 shows "999.9K", not "1M").
        const scaled = Math.floor((abs / limit) * 10) / 10;
        const text = scaled % 1 === 0 ? String(scaled) : scaled.toFixed(1);
        return sign + text + suffix;
      }
    }

    return sign + String(abs);
  }
}
